import React from "react";
import SatisfactionTrendsNew from "./components/satisfactionTrendsNew";
import WaitingTimeNew from "./components/waitingTimeNew";
import AnalyticsDatePicker from "./components/AnalyticsDatePicker";
import DepartmentSatisfaction from "./components/DepartmentSatisfaction";
import OrganizationSatisfaction from "./components/OrganizationSatisfaction";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  Button,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import {
  SGetUssdOffices,
  SGetUssdOrganizationStats,
  SGetKoboOrganizations,
  SGetKoboOrganizationStats,
  SGetKoboSurveys,
  SGetDeviceInstitutions,
  SGetDeviceOrganizationStats,
} from "../../apis/app.service";
import dayjs from "dayjs";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TimerIcon from "@mui/icons-material/Timer";
import SpeedIcon from "@mui/icons-material/Speed";
import PerformanceCard from "./components/PerformanceCard";
import { colors } from "../../theme/colors";
import { useSyncPolling } from "./components/syncState";

interface KoboSurveyOption {
  id: string;
  name: string;
  asset_uid?: string;
}

function AnalyticsPage() {
  // Global filter state
  const [dataSource, setDataSource] = React.useState("All");
  const [organization, setOrganization] = React.useState<string | null>("");
  // Kobo-only: restrict the data to a single survey (Kobo project)
  const [survey, setSurvey] = React.useState<KoboSurveyOption | null>(null);
  const surveyId = survey?.id;

  // Store refetch functions from child components
  const refetchFunctions = React.useRef<{
    koboTrends?: () => void;
    koboWaitingTime?: () => void;
  }>({});

  // Date picker state
  const [dateType, setDateType] = React.useState("month");
  const [selectedDate, setSelectedDate] = React.useState<any>(dayjs());
  const [selectedDateFormatted, setSelectedDateFormatted] = React.useState(
    dayjs().format("YYYY-MM"),
  );
  const [startDate, setStartDate] = React.useState(
    dayjs().startOf("month").format("YYYY-MM-DD"),
  );
  const [endDate, setEndDate] = React.useState(
    dayjs().endOf("month").format("YYYY-MM-DD"),
  );

  const handleDateChange = (start: string, end: string) => {
    setStartDate(start);
    setEndDate(end);
  };

  const koboSyncPolling = useSyncPolling();

  // Fetch USSD offices when USSD is selected
  const { data: ussdOfficesData, isLoading: isLoadingUssd } = useQuery({
    queryKey: ["ussdOffices", startDate, endDate],
    queryFn: () => SGetUssdOffices({ startDate, endDate }),
    enabled: dataSource === "USSD" || dataSource === "All",
  });

  // Fetch the list of Kobo surveys to populate the survey filter
  const { data: koboSurveysData, isLoading: isLoadingSurveys } = useQuery({
    queryKey: ["koboSurveys"],
    queryFn: () => SGetKoboSurveys(),
    enabled: dataSource === "Kobo",
  });

  const surveys: KoboSurveyOption[] = React.useMemo(
    () => koboSurveysData?.data || [],
    [koboSurveysData],
  );

  // Fetch Kobo organizations when Kobo is selected
  const { data: koboOrganizationsData, isLoading: isLoadingKobo } = useQuery({
    queryKey: ["koboOrganizations", startDate, endDate, surveyId],
    queryFn: () =>
      SGetKoboOrganizations({ startDate, endDate, survey: surveyId }),
    enabled: dataSource === "Kobo" || dataSource === "All",
  });

  // Fetch Device institutions when Device is selected
  const { data: deviceInstitutionsData, isLoading: isLoadingDevice } = useQuery(
    {
      queryKey: ["deviceInstitutions", startDate, endDate],
      queryFn: () => SGetDeviceInstitutions({ startDate, endDate }),
      enabled: dataSource === "Device" || dataSource === "All",
    },
  );

  // Reset organization and survey when data source changes
  React.useEffect(() => {
    setOrganization(null);
    setSurvey(null);
  }, [dataSource]);

  // Get organizations based on selected data source
  const organizations = React.useMemo(() => {
    if (dataSource === "USSD" && ussdOfficesData?.data) {
      return ussdOfficesData.data;
    }
    if (dataSource === "Kobo" && koboOrganizationsData?.data) {
      return koboOrganizationsData.data;
    }
    if (dataSource === "Device" && deviceInstitutionsData?.data) {
      return deviceInstitutionsData.data;
    }
    return [];
  }, [
    dataSource,
    ussdOfficesData,
    koboOrganizationsData,
    deviceInstitutionsData,
  ]);

  // Reset survey when it's no longer among the available surveys
  React.useEffect(() => {
    if (
      survey &&
      surveys.length > 0 &&
      !surveys.some((s) => s.id === survey.id)
    ) {
      setSurvey(null);
    }
  }, [surveys, survey]);

  // Reset organization when date range changes if it's no longer in the list
  React.useEffect(() => {
    if (
      organization &&
      organizations.length > 0 &&
      !organizations.includes(organization)
    ) {
      setOrganization(null);
    }
  }, [organizations, organization]);

  // Fetch organization statistics (only when no specific organization is selected)
  const { data: orgStatsData, isLoading: orgStatsLoading } = useQuery({
    queryKey: ["ussdOrganizationStats", startDate, endDate, organization],
    queryFn: () =>
      SGetUssdOrganizationStats({
        startDate,
        endDate,
        office: organization || undefined,
      }),
    enabled: dataSource === "USSD" || dataSource === "All",
  });

  // Fetch Kobo organization statistics with auto-refetch when syncing
  const {
    data: koboOrgStatsData,
    isLoading: koboOrgStatsLoading,
    refetch: refetchKoboOrgStats,
  } = useQuery({
    queryKey: [
      "koboOrganizationStats",
      startDate,
      endDate,
      organization,
      surveyId,
    ],
    queryFn: () =>
      SGetKoboOrganizationStats({
        startDate,
        endDate,
        organization: organization || undefined,
        survey: surveyId,
      }),
    enabled: dataSource === "Kobo" || dataSource === "All",
    // Polls while the server reports a sync, then gives up — see syncState.ts
    refetchInterval: koboSyncPolling.refetchInterval,
    // Show stale data while refetching
    refetchOnWindowFocus: false,
  });

  // Fetch Device organization statistics
  const { data: deviceOrgStatsData, isLoading: deviceOrgStatsLoading } =
    useQuery({
      queryKey: ["deviceOrganizationStats", startDate, endDate],
      queryFn: () =>
        SGetDeviceOrganizationStats({
          startDate,
          endDate,
        }),
      enabled: dataSource === "Device" || dataSource === "All",
    });

  // Calculate best/worst performers
  const performers = React.useMemo(() => {
    if (dataSource === "All") {
      // Combine data from all sources
      const allSatisfaction = [];
      const allWaitingTime = [];

      if (orgStatsData?.data?.satisfaction) {
        allSatisfaction.push(...orgStatsData.data.satisfaction);
      }
      if (koboOrgStatsData?.data?.satisfaction) {
        allSatisfaction.push(...koboOrgStatsData.data.satisfaction);
      }
      if (deviceOrgStatsData?.data?.satisfaction) {
        allSatisfaction.push(...deviceOrgStatsData.data.satisfaction);
      }

      if (orgStatsData?.data?.waitingTime) {
        allWaitingTime.push(...orgStatsData.data.waitingTime);
      }
      if (koboOrgStatsData?.data?.waitingTime) {
        allWaitingTime.push(...koboOrgStatsData.data.waitingTime);
      }

      // Sort combined data
      allSatisfaction.sort((a, b) => b.satisfactionScore - a.satisfactionScore);
      allWaitingTime.sort((a, b) => b.fastServiceScore - a.fastServiceScore);

      return {
        bestSatisfaction: allSatisfaction[0] || null,
        worstSatisfaction: allSatisfaction[allSatisfaction.length - 1] || null,
        fastestService: allWaitingTime[0] || null,
        slowestService: allWaitingTime[allWaitingTime.length - 1] || null,
      };
    }

    const statsData =
      dataSource === "USSD"
        ? orgStatsData
        : dataSource === "Kobo"
          ? koboOrgStatsData
          : deviceOrgStatsData;
    if (!statsData?.data) return null;

    const { satisfaction, waitingTime } = statsData.data;

    // Find best and worst satisfaction
    const bestSatisfaction =
      satisfaction && satisfaction.length > 0 ? satisfaction[0] : null;
    const worstSatisfaction =
      satisfaction && satisfaction.length > 0
        ? satisfaction[satisfaction.length - 1]
        : null;

    // Find fastest and slowest service (only for USSD and Kobo)
    const fastestService =
      waitingTime && waitingTime.length > 0 ? waitingTime[0] : null;
    const slowestService =
      waitingTime && waitingTime.length > 0
        ? waitingTime[waitingTime.length - 1]
        : null;

    return {
      bestSatisfaction,
      worstSatisfaction,
      fastestService,
      slowestService,
    };
  }, [dataSource, orgStatsData, koboOrgStatsData, deviceOrgStatsData]);

  // Track syncing status from child components
  const [childSyncingStates, setChildSyncingStates] = React.useState<{
    trends: boolean;
    waitingTime: boolean;
    departments: boolean;
    ratingMix: boolean;
  }>({
    trends: false,
    waitingTime: false,
    departments: false,
    ratingMix: false,
  });

  // Page-level view of the Kobo sync, aggregated from this query and the charts.
  const koboSync = koboSyncPolling.resolve(koboOrgStatsData);

  const isAnySyncing = React.useMemo(() => {
    if (dataSource === "Kobo" || dataSource === "All") {
      return (
        koboSync.state === "syncing" ||
        childSyncingStates.trends ||
        childSyncingStates.waitingTime ||
        childSyncingStates.departments ||
        childSyncingStates.ratingMix
      );
    }
    return false;
  }, [dataSource, koboSync.state, childSyncingStates]);

  // A sync that failed or outlasted its polling budget: the charts each show their own
  // notice, and the page offers one control that refreshes all of them at once.
  const syncStalled =
    (dataSource === "Kobo" || dataSource === "All") &&
    (koboSync.state === "failed" || koboSync.state === "stalled");

  // Function to refetch all Kobo data
  const refetchAllKoboData = React.useCallback(() => {
    koboSyncPolling.reset();
    refetchKoboOrgStats();
    if (refetchFunctions.current.koboTrends) {
      refetchFunctions.current.koboTrends();
    }
    if (refetchFunctions.current.koboWaitingTime) {
      refetchFunctions.current.koboWaitingTime();
    }
  }, [koboSyncPolling, refetchKoboOrgStats]);

  // Stable callbacks for child components
  const handleTrendsRefetch = React.useCallback((refetchFn: () => void) => {
    refetchFunctions.current.koboTrends = refetchFn;
  }, []);

  const handleWaitingTimeRefetch = React.useCallback(
    (refetchFn: () => void) => {
      refetchFunctions.current.koboWaitingTime = refetchFn;
    },
    [],
  );

  const handleTrendsSyncStatusChange = React.useCallback(
    (isSyncing: boolean) => {
      setChildSyncingStates((prev) => ({ ...prev, trends: isSyncing }));
    },
    [],
  );

  const handleWaitingTimeSyncStatusChange = React.useCallback(
    (isSyncing: boolean) => {
      setChildSyncingStates((prev) => ({ ...prev, waitingTime: isSyncing }));
    },
    [],
  );

  const handleDepartmentsSyncStatusChange = React.useCallback(
    (isSyncing: boolean) => {
      setChildSyncingStates((prev) => ({ ...prev, departments: isSyncing }));
    },
    [],
  );

  const handleRatingMixSyncStatusChange = React.useCallback(
    (isSyncing: boolean) => {
      setChildSyncingStates((prev) => ({ ...prev, ratingMix: isSyncing }));
    },
    [],
  );

  return (
    <Box sx={{ backgroundColor: "#f5f7fa", minHeight: "100vh" }}>
      {/* Header Section */}
      <Box
        sx={{
          backgroundColor: "white",
          borderBottom: "1px solid #e0e0e0",
          padding: "2rem 2.5rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{ fontWeight: 600, color: "#1a1a1a", mb: 0.5 }}
            >
              Analytics
            </Typography>
            <Typography variant="body1" sx={{ color: "#666" }}>
              Comprehensive insights into customer satisfaction and service
              performance
            </Typography>
          </Box>
        </Box>

        {/* Global Filters */}
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "flex-end",
            // pt: 2,
            // borderTop: "1px solid #f0f0f0",
          }}
        >
          {/* data source */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#444" }}
            >
              Data Source
            </Typography>
            <select
              value={dataSource}
              onChange={(e) => setDataSource(e.target.value)}
              style={{
                padding: "0.625rem 0.75rem",
                borderRadius: "6px",
                border: "1px solid #d0d0d0",
                minWidth: "150px",
                fontSize: "0.875rem",
                backgroundColor: "white",
                cursor: "pointer",
              }}
            >
              <option value="All">All Sources</option>
              <option value="USSD">USSD</option>
              <option value="Kobo">Kobo</option>
              <option value="Device">Device</option>
            </select>
          </Box>

          {/* survey (Kobo only) */}
          {dataSource === "Kobo" && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#444" }}
              >
                Survey
              </Typography>
              <Autocomplete
                options={surveys}
                value={survey}
                onChange={(event, newValue) => setSurvey(newValue)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                loading={isLoadingSurveys}
                size="small"
                sx={{ minWidth: 220 }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      isLoadingSurveys ? "Loading..." : "All surveys"
                    }
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "6px",
                        "& fieldset": {
                          borderColor: "#d0d0d0",
                        },
                      },
                    }}
                  />
                )}
              />
            </Box>
          )}

          {/* organization */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#444" }}
            >
              {dataSource === "Device" ? "Institution" : "Organization"}
            </Typography>
            <Autocomplete
              options={organizations}
              value={organization}
              onChange={(event, newValue) => setOrganization(newValue)}
              disabled={!dataSource || dataSource === "All"}
              loading={isLoadingUssd || isLoadingKobo || isLoadingDevice}
              size="small"
              sx={{ minWidth: 200 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={
                    isLoadingUssd || isLoadingKobo || isLoadingDevice
                      ? "Loading..."
                      : dataSource === "USSD" ||
                          dataSource === "Kobo" ||
                          dataSource === "Device"
                        ? "Search organizations..."
                        : "Select data source first"
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "6px",
                      "& fieldset": {
                        borderColor: "#d0d0d0",
                      },
                    },
                  }}
                />
              )}
            />
          </Box>

          {/* date filter */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#444" }}
            >
              Date Range
            </Typography>
            <AnalyticsDatePicker
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              selectedDateFormatted={selectedDateFormatted}
              setSelectedDateFormatted={setSelectedDateFormatted}
              type={dateType}
              setType={setDateType}
              onDateChange={handleDateChange}
              disabled={!dataSource}
            />
          </Box>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ padding: "2rem 2.5rem", backgroundColor: colors.white }}>
        {/* Non-blocking refresh indicator — shows only when cache is being refreshed in background */}
        {isAnySyncing && (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#e3f2fd",
              border: "1px solid #90caf9",
              borderRadius: "20px",
              padding: "0.4rem 1rem",
              mb: 2,
              fontSize: "0.8rem",
              color: "#1565c0",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#1976d2",
                display: "inline-block",
                animation: "pulse 1.5s infinite",
              }}
            />
            Syncing latest data in background…
          </Box>
        )}
        {/* Terminal state: the sync stopped without producing data. Say so, and offer a
            way to try again — the alternative is reloading the page to find out. */}
        {syncStalled && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flexWrap: "wrap",
              backgroundColor:
                koboSync.state === "failed" ? "#fdecea" : "#fff8e1",
              border: `1px solid ${
                koboSync.state === "failed" ? "#f5c6c3" : "#ffe0a3"
              }`,
              borderRadius: "8px",
              padding: "0.6rem 1rem",
              mb: 2,
              fontSize: "0.8rem",
              color: koboSync.state === "failed" ? "#ad2117" : "#8a5b00",
            }}
          >
            <span>
              {koboSync.message ||
                (koboSync.state === "failed"
                  ? "The last sync of this period failed."
                  : "The sync is taking longer than expected.")}
            </span>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={refetchAllKoboData}
              sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.1 }}
            >
              Check again
            </Button>
          </Box>
        )}
        {/* Performance Cards */}
        {(dataSource === "USSD" ||
          dataSource === "Kobo" ||
          dataSource === "Device" ||
          dataSource === "All") && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns:
                dataSource === "Device"
                  ? "repeat(auto-fit, minmax(280px, 1fr))"
                  : "repeat(auto-fit, minmax(280px, 1fr))",
              gridAutoRows: "minmax(180px, auto)",
              gap: 2.5,
              mb: 3,
            }}
          >
            {/* Best Satisfaction */}
            <PerformanceCard
              title={`Best Satisfaction ${organization || ""}`}
              icon={<TrendingUpIcon sx={{ color: "#388e3c", fontSize: 28 }} />}
              borderColor="#388e3c"
              loading={
                orgStatsLoading || koboOrgStatsLoading || deviceOrgStatsLoading
              }
              data={performers?.bestSatisfaction || null}
              metricLabel="positive ratings"
            />

            {/* Worst Satisfaction */}
            <PerformanceCard
              title={`Needs Improvement ${organization || ""}`}
              icon={
                <TrendingDownIcon sx={{ color: "#d32f2f", fontSize: 28 }} />
              }
              borderColor="#d32f2f"
              loading={
                orgStatsLoading || koboOrgStatsLoading || deviceOrgStatsLoading
              }
              data={performers?.worstSatisfaction || null}
              metricLabel="positive ratings"
            />

            {/* Fastest Service - Only for USSD and Kobo */}
            {dataSource !== "Device" && (
              <PerformanceCard
                title={`Fastest Service ${organization || ""}`}
                icon={<SpeedIcon sx={{ color: "#1976d2", fontSize: 28 }} />}
                borderColor="#1976d2"
                loading={orgStatsLoading || koboOrgStatsLoading}
                data={performers?.fastestService || null}
                metricLabel="under 30 min"
              />
            )}

            {/* Slowest Service - Only for USSD and Kobo */}
            {dataSource !== "Device" && (
              <PerformanceCard
                title={`Slowest Service ${organization || ""}`}
                icon={<TimerIcon sx={{ color: "#f57c00", fontSize: 28 }} />}
                borderColor="#f57c00"
                loading={orgStatsLoading || koboOrgStatsLoading}
                data={performers?.slowestService || null}
                metricLabel="under 30 min"
              />
            )}
          </Box>
        )}

        {/* Satisfaction Trends */}
        <Box sx={{ mb: 3 }}>
          <SatisfactionTrendsNew
            dataSource={dataSource}
            organization={organization}
            survey={surveyId}
            startDate={startDate}
            endDate={endDate}
            dateType={dateType}
            onRefetch={handleTrendsRefetch}
            onSyncStatusChange={handleTrendsSyncStatusChange}
          />
        </Box>

        {/* Survey-specific breakdowns - side by side, stacking on narrower screens */}
        {dataSource === "Kobo" && surveyId && (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(560px, 1fr))",
              gap: 2.5,
              mb: 3,
            }}
          >
            <DepartmentSatisfaction
              survey={surveyId}
              organization={organization}
              startDate={startDate}
              endDate={endDate}
              onSyncStatusChange={handleDepartmentsSyncStatusChange}
            />
            <OrganizationSatisfaction
              survey={surveyId}
              organization={organization}
              startDate={startDate}
              endDate={endDate}
              onSyncStatusChange={handleRatingMixSyncStatusChange}
            />
          </Box>
        )}

        {/* Waiting Time - Hide for Device data source */}
        {dataSource !== "Device" && (
          <Box>
            <WaitingTimeNew
              dataSource={dataSource}
              organization={organization}
              survey={surveyId}
              startDate={startDate}
              endDate={endDate}
              onRefetch={handleWaitingTimeRefetch}
              onSyncStatusChange={handleWaitingTimeSyncStatusChange}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default AnalyticsPage;
